// @flow
import * as React from 'react'
import { Route } from "react-router-dom"

import * as THREE from 'three'
import Article from '../Article/'
import Detail from '../Detail/'
import Photographer from '../Photographer/'
import About from '../About/'
import Navbar from './navbar'
import BigDate from './bigDate'
import ThreeApp from '../../utils/threeApp.js'
import DailyItemsApp from '../../utils/dailyItemsApp.js'
import { daysSinceMonthStarted } from '../../utils/parsingUtils.js'
import CameraApp from '../../utils/cameraApp.js'

import logo from '../../assets/images/logo.svg'
import grid from '../../assets/images/grid.svg'
import mglass from '../../assets/images/mglass.svg'

import type MouseStates from '../../utils/MouseStates.js'
import type { DivRefType, MouseType } from 'react-helper'
import type PhotographersApp  from '../../utils/photographersApp'
import type { ContextRouter } from 'react-router-dom'
import type { PositionType } from 'application-helper'
import type { DailyItemType } from 'database-helper'

import './styles.css'

type PropsType = {
  ...ContextRouter,
  PhotographersApp: PhotographersApp,
  DailyItemsApp: DailyItemsApp,
  textures: Array<THREE.Texture>,
  canAnimate: boolean,
  setPlaneCreated: (uid: string) => void,
  triggerFirstAppearance: boolean,
  MouseStates: MouseStates
}

type StateType = {
  scene: THREE.Scene,
  renderer: THREE.WebGLRenderer,
  camera: ?THREE.PerspectiveCamera,
  rayCaster: THREE.Raycaster,
  hoveredItem: ?THREE.Object3D,
  detailView: boolean,
  ThreeApp: ThreeApp,
  navbarSelectorPos: number,
  navbarClicked: boolean,
  planesCreated: Array<string>
}

export default class Home extends React.Component<PropsType, StateType> {
  rendererContainerRef: DivRefType
  navbarContainerRef: DivRefType
  navbarMinHeight: number
  navbarMaxHeight: number
  rafID: ?AnimationFrameID
  mouse: MouseType
  CameraApp: ?CameraApp
  navMouse: boolean
  setNavbarClickedAvailable: boolean // because function can be called form 2 different places
  constructor(props: PropsType) {
    super(props)
    this.rendererContainerRef = React.createRef()
    this.navbarContainerRef = React.createRef()
    this.mouse = { x: 0, y: 0 }
    this.CameraApp = undefined
    this.navbarMaxHeight = 1000
    this.navbarMinHeight = 0
    this.navMouse = false;
    this.setNavbarClickedAvailable = true

    this.state = {
      scene: new THREE.Scene(),
      renderer: ThreeApp.createRenderer(),
      camera: undefined,
      rayCaster: new THREE.Raycaster(),
      hoveredItem: undefined,
      detailView: false,
      ThreeApp: new ThreeApp(),
     
      navbarSelectorPos: 0,
      navbarClicked: false,
      planesCreated: [],
    }
  }
 
  componentDidMount() {
    window.addEventListener('resize', this.windowResized)
    this.state.renderer.setPixelRatio(1)
    this.state.ThreeApp.setContainer(this.rendererContainerRef)
    this.state.ThreeApp.setRendererSize(this.state.renderer)
    this.state.ThreeApp.appendRenderer(this.state.renderer)
    if (this.navbarContainerRef.current) this.setMaxAndMinHeightForNavbar(this.navbarContainerRef.current)
    this.setInitialCamPos()
  }

  componentDidUpdate(prevProps: PropsType, prevState: StateType) {
    const camera = this.state.camera
    const cameraApp = this.CameraApp
    if (camera !== undefined && !prevProps.canAnimate && this.props.canAnimate) this.animateScene()
    if (prevState.navbarSelectorPos !== this.state.navbarSelectorPos) this.setCameraThroughSelectorPos(this.state.navbarSelectorPos)
    if (!prevProps.triggerFirstAppearance && this.props.triggerFirstAppearance && camera !== undefined && cameraApp instanceof CameraApp) this.firstAppearanceCamera(camera, cameraApp)
  }

  componentWillUnmount () {
    window.removeEventListener('resize', this.windowResized)
  }

  windowResized = () => {
    const container = this.rendererContainerRef.current
    const camera = this.state.camera
    if (container && camera) {
      this.state.ThreeApp.setResponsive(container, camera, this.state.renderer)
    }
  }

  firstAppearanceCamera = (baseCamera: THREE.PerspectiveCamera, cameraApp: CameraApp) => {
    this.setOverviewItem(this.props.DailyItemsApp.getDailyItem())
    const camera = this.state.ThreeApp.setBaseCameraPosition(baseCamera, this.props.DailyItemsApp.keys.length)
    const location = this.props.location.pathname
    const splitted = location.split('/')
    const uid = splitted[splitted.length - 1]
    const zPos = this.props.match.isExact
                  ? this.state.ThreeApp.getUpdatedZPos(this.props.DailyItemsApp.getOverviewItemIndex(), this.props.DailyItemsApp.getOverviewItemIndex()) + this.state.ThreeApp.CAM_AT
                  : this.getArticlePosition(uid, camera)
    this.setCamera({z: zPos, y: baseCamera.position.y})
  }

  setInitialCamPos = () => {
    const baseCamera = this.state.ThreeApp.createCamera(this.props.DailyItemsApp.keys.length)
    const camera = this.state.ThreeApp.setBaseCameraPosition(baseCamera, this.props.DailyItemsApp.keys.length)
    this.CameraApp = new CameraApp(
      camera,
      this.state.scene,
      [camera.position.z, 0],
      this.updateCamera
    )
    this.setState({ camera })
  }

  resetCamera = () => {
    const cameraApp = this.CameraApp
    if (cameraApp) {
      const firstItem = this.props.DailyItemsApp.getDailyItemThroughIndex(this.props.DailyItemsApp.keys.length - 1)
      const height = cameraApp.planeHeight
      const pos = this.state.ThreeApp.getXYZPos(this.props.DailyItemsApp.getItemIndex(firstItem.uid), this.props.DailyItemsApp.getOverviewItemIndex(), height)
      cameraApp.setAutomatic(false)
      this.setCamera({z: pos.z + this.state.ThreeApp.CAM_AT, y: pos.y})
    }
  }

  getArticlePosition = (uid: string, camera: THREE.Camera): number => {
    const index = this.props.DailyItemsApp.keys.indexOf(uid)
    // if (index === -1) return this.state.ThreeApp.getZPos(this.props.DailyItemsApp.keys.length - 1) + this.state.ThreeApp.CAM_AT
    if (index === -1) return this.state.ThreeApp.getUpdatedZPos(this.props.DailyItemsApp.getOverviewItemIndex(), this.props.DailyItemsApp.getOverviewItemIndex()) + this.state.ThreeApp.CAM_AT
    // const zPos = this.state.ThreeApp.getZPos(index)
    const zPos = this.state.ThreeApp.getUpdatedZPos(index, this.props.DailyItemsApp.getOverviewItemIndex())
    return this.state.ThreeApp.setCameraForFocus(zPos, camera).position.z
  }

  animateScene = () => {
    if (!this.rafID && this.state.camera && this.CameraApp) {
      this.rafID = this.state.ThreeApp.animate(
        this.getRenderer,
        this.getScene,
        this.getCamera,
        this.getRaycaster,
        this.getMouse,
        this.updateRafID,
        this.updateIntersects,
        this.CameraApp
      )
    }
  }

  stopScene = () => {
    if (this.rafID) {
      cancelAnimationFrame(this.rafID)
      this.rafID = undefined
    }
  }

  updateRafID = (rafID: AnimationFrameID) => {
    this.rafID = rafID
  }

  updateIntersects = (raycaster: THREE.Raycaster) => {
    const hoveredItem = raycaster.intersectObjects(this.state.scene.children)[0]
    // console.info(this.props.MouseStates);

    if(hoveredItem) {
      this.props.MouseStates.handleMouseIntersectPlane(true);
    } else {
      this.props.MouseStates.handleMouseIntersectPlane(false);
    }

    if (this.state.hoveredItem !== hoveredItem)
      this.setState({ hoveredItem: hoveredItem ? hoveredItem.object : undefined })
  }

  updateCamera = (pos: {z: number, y: number}) => {
    const oldCamera = this.state.camera
    if (!oldCamera) return undefined
    const camera = this.state.ThreeApp.setCameraZYPos(oldCamera, pos)
    this.props.DailyItemsApp.cameraUpdated(camera.position.z, this.state.ThreeApp, this.state.scene)
    this.setState({ camera })
  }

  moveCamera = (e: WheelEvent) => {
    if (this.state.detailView) return null
    const cameraApp = this.CameraApp
    if (cameraApp) {
      const value = this.state.ThreeApp.CAMERA_STEP * (Math.abs(e.deltaY) / 50)
      const direction = e.deltaY < 0 ? -1 : 1
      cameraApp.setAutomatic(true)
      cameraApp.setDirection(direction)
      cameraApp.updateGoal({
        z: value * direction,
        y: 0 // @TODO --> good ?
      })
    }
  }

  setCamera = (pos: {z: number, y: number}) => {
    const cameraApp = this.CameraApp
    if (cameraApp) {
      cameraApp.setUpdateGoal({z: pos.z, y: pos.y})
    }
  }

  getRenderer = (): THREE.WebGLRenderer => {
    return this.state.renderer
  }

  getScene = (): THREE.Scene => {
    return this.state.scene
  }

  getCamera = (): THREE.PerspectiveCamera => {
    return this.state.camera
  }
 
  getRaycaster = (): THREE.Raycaster => {
    return this.state.rayCaster
  }

  getMouse = (): MouseType => {
    return this.mouse
  }

  onWheel = (e: WheelEvent) => {
    e.preventDefault()
    this.moveCamera(e)
  }

  setMouseForRaycasting = (e: MouseEvent) => {
    const container = this.rendererContainerRef.current
    if (!container) return null
    this.mouse = {
      x: (e.clientX / container.clientWidth) * 2 - 1,
      y: - (e.clientY / container.clientHeight) * 2 + 1
    }
  }

  setMouseForNavbar = (e: MouseEvent, force: ?boolean) => {
    const container = this.navbarContainerRef.current
    const navbarClicked = force || this.state.navbarClicked

    if (navbarClicked && container) {
      this.navMouse = true;
      const clientRect = container.getBoundingClientRect()
      const mousePos = e.clientY - clientRect.top
      const navbarSelectorPos = this.getNavbarSelectorPos(mousePos, clientRect)
      this.setState({ navbarSelectorPos })
    } else {
      this.navMouse = false;
    }
  }

  setOverviewItem = (dailyItem: DailyItemType) => {
    const oldOverviewItem = this.props.DailyItemsApp.getOverviewItem()
    const oldOverviewItemIndex = this.props.DailyItemsApp.getItemIndex(oldOverviewItem.uid)
    
    const dailyItemIndex = this.props.DailyItemsApp.getItemIndex(dailyItem.uid)
    const cameraApp = this.CameraApp
    const pos = cameraApp
      ? this.state.ThreeApp.getXYZPos(dailyItemIndex, oldOverviewItemIndex, cameraApp.getPlaneHeight())
      : this.state.ThreeApp.getPlanePos(dailyItem.uid, this.state.scene)
    this.state.ThreeApp.setReferenceItemPos(pos)
    this.props.DailyItemsApp.setOverviewItem(dailyItem)
  }

  /*
  ** @TODO --> Method can is called twice when cursor is ON navbar.
  ** maybe see if e.stopPropagation() can do the work...
  ** onMouseHover should set a boolean to check where method should be called
  */
  setNavbarClicked = (e: ?MouseEvent, navbarClicked: boolean) => {
    if (!this.setNavbarClickedAvailable) return undefined
    this.setNavbarClickedAvailable = false
    this.setOverviewItem(this.props.DailyItemsApp.getDailyItem())
    this.setNavbarView(navbarClicked, e)
    if (e) this.setMouseForNavbar(e, true)
    const cameraApp = this.CameraApp
    if (cameraApp) {
      cameraApp.setDirection(1)
      cameraApp.setAutomatic(!navbarClicked)
    }
    this.setNavbarClickedAvailable = true
    this.setState({ navbarClicked })
  }

  setNavbarView = (navbarView: boolean, e: ?MouseEvent) => {
    this.state.ThreeApp.setNavbarView(navbarView)
    const selectorPos = this.state.navbarSelectorPos
    const dailyItem = e ? this.props.DailyItemsApp.getOverviewItem() : this.props.DailyItemsApp.getDailyItem()
    if (selectorPos != null && navbarView) {
      const index = this.props.DailyItemsApp.getItemIndex(dailyItem.uid)
      this.setCameraThroughSelectorPos(selectorPos, index)
    }
    else {
      const index = this.props.DailyItemsApp.getOverviewItemIndex()
      const height = this.CameraApp ? this.CameraApp.planeHeight : 0
      const pos = this.state.ThreeApp.getXYZPos(index + 1, index, height) // index + 1 to correct display bug
      this.setCamera({z: pos.z - (this.state.ThreeApp.CAM_AT * 2.5), y: pos.y}) // CAM_AT to forward a bit camera position on release
    }
  }

  setMaxAndMinHeightForNavbar = (container: HTMLDivElement) => {
    const passedDays = daysSinceMonthStarted(this.props.DailyItemsApp.getDailyItem().uid) || 1
    this.navbarMinHeight = passedDays * container.getBoundingClientRect().height / 365
    this.navbarMaxHeight = (this.props.DailyItemsApp.keys.length * container.getBoundingClientRect().height / 365) + this.navbarMinHeight
  }

  getNavbarSelectorPos = (mousePos: number, clientRect: ClientRect): number => {
    const container = this.navbarContainerRef.current
    if (container) {
      if (mousePos < this.navbarMinHeight) return this.navbarMinHeight
      else if (mousePos > this.navbarMaxHeight) return this.navbarMaxHeight
      else return mousePos
    }
    return 0
  }
  
  setCameraThroughSelectorPos = (selectorPos: number, forcedIndex: ?number) => {
    const container = this.navbarContainerRef.current
    if (container) {
      const index = forcedIndex != null ? forcedIndex : this.props.DailyItemsApp.keys.length - this.getNavbarIndex(selectorPos, container)
      const cameraApp = this.CameraApp
      const planeHeight = cameraApp ? cameraApp.planeHeight : 0
      const overviewItem = this.props.DailyItemsApp.getOverviewItem()
      const overviewItemIndex = this.props.DailyItemsApp.getItemIndex(overviewItem.uid)

      const z = this.state.ThreeApp.getUpdatedZPos(index, overviewItemIndex)
      const y = this.state.ThreeApp.getUpdatedYPos(index - 2, overviewItemIndex, planeHeight) // -2 to see scene from above


      this.setCamera({z, y})
    }
  }

  getNavbarIndex = (selectorPos: number, navbarContainer: HTMLDivElement) =>
    (selectorPos - this.navbarMinHeight) * this.props.DailyItemsApp.keys.length / (this.navbarMaxHeight - this.navbarMinHeight)

  onMouseMove = (e: MouseEvent) => {
    this.setMouseForRaycasting(e)
    this.setMouseForNavbar(e)
  }

  onClick = () => {
    const oldCamera = this.state.camera
    const hoveredItem = this.state.hoveredItem
    if (hoveredItem && !this.state.detailView && oldCamera) {
      if (this.CameraApp) this.CameraApp.setAutomatic(false)
      if (hoveredItem.name === this.props.DailyItemsApp.keys[this.props.DailyItemsApp.keys.length - 1] && this.state.ThreeApp) this.state.ThreeApp.setFirstArticleClicked()
      const camera = this.state.ThreeApp.setCameraForFocus(hoveredItem.position.z, oldCamera)
      this.setCamera({ z: camera.position.z, y: hoveredItem.position.y })
      this.props.history.push(`/article/${hoveredItem.name}`)
      this.props.MouseStates.setShowArticle(true)
    }
  }

  getContainerBG = (DailyItemsApp: ?DailyItemsApp): string =>
    `rgb(${this.props.DailyItemsApp.dailyItem.color.r}, ${this.props.DailyItemsApp.dailyItem.color.g}, ${this.props.DailyItemsApp.dailyItem.color.b})`

  returnToArticle = () => {
    this.props.history.push('/')
    const cameraApp = this.CameraApp
    if (cameraApp) {
      cameraApp.setAutomatic(true)
      cameraApp.setDirection(1)
    }
    this.setState({ detailView: false })
  }

  renderAllArticles = (props: ContextRouter) => {
    const camera = this.state.camera
    const cameraApp = this.CameraApp
    if (!camera || !cameraApp) return null
    return this.props.DailyItemsApp.keys.map((uid: string, index: number) => {
      const dailyItem = this.props.DailyItemsApp.getDailyItemThroughUID(uid)
      return dailyItem 
        ?
          <Article
            {...props}
            uid={uid}
            key={uid}
            index={index}
            dailyItem={dailyItem}
            scene={this.state.scene}
            camera={this.state.camera}
            ThreeApp={this.state.ThreeApp}
            DailyItemsApp={this.props.DailyItemsApp}
            lastArticleIndex={this.props.DailyItemsApp.keys.length - 1}
            navbarClicked={this.state.navbarClicked}
            CameraApp={cameraApp}
            texture={this.props.textures[index]}
            setPlaneCreated={this.props.setPlaneCreated}
          />
        :
          null
    })
  }

  renderHome = () => {
    return (
      <div
        className="home-content-container"
        onWheel={(e) => this.onWheel(e)}
        onClick={() => this.onClick()}
        style={{ userSelect: 'none' }}
      >
        { this.renderAllArticles({
          history: this.props.history,
          location: this.props.location,
          match: this.props.match
        }) }
      </div>
    )
  }

  getInterfaceClassName = () =>
    `home-interface-container ${this.props.match.isExact ? '' : 'hidden'}`

  
  renderInterface() {
    const dailyItems = this.props.DailyItemsApp.getDailyItems()
    const dailyItem = this.props.DailyItemsApp.getDailyItem()

    return (
      <div className={this.getInterfaceClassName()}>
        <div className="home-interface-actions">
          <img src={logo} alt="logo" className="logo" onClick={this.resetCamera}/>
          <img src={grid} alt="logo" className="action"/>
          <img src={mglass} alt="logo" className="action"/>
          {dailyItems && dailyItem && [
            <div className="date" key="date">
              <BigDate date={dailyItem.uid} />
            </div>,
            <div className="navbar" key="navbar">
              <Navbar
                DailyItemApp={this.props.DailyItemsApp}
                navbarContainerRef={this.navbarContainerRef}
                setNavbarClicked={this.setNavbarClicked}
                navbarClicked={this.state.navbarClicked}
                selectorPos={this.state.navbarSelectorPos}
                navMouse={this.navMouse}
              />
            </div>
          ]}
        </div>
        <h3 className="about-btn" onClick={() => { this.props.history.push('/about') }}>ABOUT</h3>
      </div>
    )
  }

  handleHoverDot(bool: boolean) {
    // this.setState({onHoverDot: bool});
  }

  getOverviewPos = (uid: string): PositionType => {
    const scene = this.state.scene
    if (scene) {
      const plane = scene.getObjectByName(uid)
      if (plane) return plane.position
    }
    return {x: NaN, y: NaN, z: NaN}
  }

  render() {
    return (
      <div
        className="home-container"
        onMouseMove={(e) => this.onMouseMove(e)}
        onMouseUp={() => {
          if (this.props.match.isExact && this.state.navbarClicked) {
            this.setNavbarClicked(undefined, false)
          }
        }}
        style={{ backgroundColor: this.getContainerBG(this.props.DailyItemsApp) }}
      >
        <Route path="/about" render={props =>(
            <About
              {...props}
              returnToArticle={this.returnToArticle}
              CameraApp={this.CameraApp}
            />

        )} />
        <Route path="/photographer/:uid" render={props =>(
            <Photographer
              {...props}
              DailyItemsApp={this.props.DailyItemsApp}
              PhotographersApp={this.props.PhotographersApp}
              returnToArticle={this.returnToArticle}
            />

        )} />
        {this.renderInterface()}
        <Route path="/article/:uid" render={props =>
          <Detail
            {...props}
            dailyItem={this.props.DailyItemsApp.getDailyItemThroughUID(props.match.params.uid)}
            returnToArticle={this.returnToArticle}
            PhotographersApp={this.props.PhotographersApp}
            MouseStates={this.props.MouseStates}
          />
        }/>
        <div ref={this.rendererContainerRef} className="home-renderer-container"/>
        {this.renderHome()}
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/Home/index.jsx